/**
 * =====================================================
 * Custom Size Form Component (Variant-Triggered)
 * For RosyMae Theme
 * =====================================================
 *
 * Shows measurement form when user selects "Custom Size" variant.
 * No modal - form displays inline below variant picker.
 */

(function () {
  'use strict';

  class CustomSizeForm {
    constructor(container) {
      this.container = container;
      this.form = container.querySelector('[data-custom-size-form]');
      this.unitBtns = container.querySelectorAll('[data-unit]');
      this.inputs = container.querySelectorAll('[data-custom-size-input]');
      this.unitInput = container.querySelector('[data-custom-size-unit]');
      this.laterCheckbox = container.querySelector('[data-custom-size-later]');
      this.clearBtn = container.querySelector('[data-custom-size-clear]');

      this.currentUnit = 'in';
      this.measurements = {};
      this.isLater = false;

      this.init();
    }

    init() {
      // Listen for variant changes
      this.setupVariantListener();

      // Unit toggle buttons
      this.unitBtns.forEach((btn) => {
        btn.addEventListener('click', () => this.switchUnit(btn.dataset.unit));
      });

      // Clear button
      this.clearBtn?.addEventListener('click', () => this.clearForm());

      // Form submission
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSubmit();
      });

      // Later checkbox
      this.laterCheckbox?.addEventListener('change', (e) => {
        this.handleLaterOption(e);
      });

      // Input changes
      this.inputs.forEach((input) => {
        input.addEventListener('input', () => this.handleInputChange(input));
      });

      // Check initial variant state
      this.checkCurrentVariant();
    }

    /**
     * Setup listener for variant changes
     */
    setupVariantListener() {
      // Method 1: Listen to variant select change
      const variantSelect = document.querySelector('[data-variant-select]');
      if (variantSelect) {
        variantSelect.addEventListener('change', () => this.checkCurrentVariant());
      }

      // Method 2: Listen to URL parameter changes
      window.addEventListener('popstate', () => this.checkCurrentVariant());

      // Method 3: Listen to Shopify variant change events
      document.addEventListener('shopify:variant:change', () => this.checkCurrentVariant());

      // Method 4: Observe variant input changes (for swatch/chip buttons)
      const observer = new MutationObserver(() => {
        setTimeout(() => this.checkCurrentVariant(), 100);
      });

      const variantWrapper = document.querySelector('[data-product-option]');
      if (variantWrapper) {
        observer.observe(variantWrapper, { childList: true, subtree: true, attributes: true });
      }

      // Method 5: Listen to URL changes
      const urlObserver = new MutationObserver(() => {
        this.checkCurrentVariant();
      });
      urlObserver.observe(document.querySelector('title') || document.head, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    /**
     * Check if current variant is "Custom Size"
     */
    checkCurrentVariant() {
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      if (variantId) {
        // Get variant data from page
        const productJson = document.querySelector('[data-product-json]') ||
                           document.querySelector('script[type="application/json"][id*="ProductJson"]');

        if (productJson) {
          try {
            const product = JSON.parse(productJson.textContent);
            const variant = product.variants.find(v => v.id == variantId);

            if (variant) {
              this.toggleForm(variant.title.toLowerCase().includes('custom size'));
              return;
            }
          } catch (e) {
            console.error('Error parsing product JSON:', e);
          }
        }
      }

      // Fallback: Check option values
      const selectedValues = [];
      document.querySelectorAll('[data-product-option] select, [data-product-option] input:checked').forEach(el => {
        if (el.value) selectedValues.push(el.value.toLowerCase());
      });

      const isCustomSize = selectedValues.some(v => v.includes('custom size'));
      this.toggleForm(isCustomSize);
    }

    /**
     * Show/hide the measurement form
     */
    toggleForm(show) {
      // Check if mobile
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      if (isMobile) {
        // Mobile: use modal popup
        if (show) {
          this.openMobileModal();
        } else {
          this.closeMobileModal();
          this.clearForm();
        }
      } else {
        // Desktop: inline display (original behavior)
        if (show) {
          this.container.style.display = 'block';
          this.container.classList.add('is-active');
        } else {
          this.container.style.display = 'none';
          this.container.classList.remove('is-active');
          this.clearForm();
        }
      }
    }

    /**
     * Check if mobile modal exists
     */
    isMobileModalOpen() {
      return document.querySelector('.custom-size-mobile-modal') !== null;
    }

    /**
     * Open mobile modal popup
     */
    openMobileModal() {
      // Don't open if already open
      if (this.isMobileModalOpen()) return;

      // Create modal structure
      const modal = document.createElement('div');
      modal.className = 'custom-size-mobile-modal';
      modal.innerHTML = `
        <div class="custom-size-mobile-modal__overlay" data-mobile-modal-close></div>
        <div class="custom-size-mobile-modal__container">
          <button type="button" class="custom-size-mobile-modal__close" data-mobile-modal-close aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div class="custom-size-mobile-modal__body" data-mobile-modal-body></div>
        </div>
      `;

      document.body.appendChild(modal);

      // Get body and clone content (form first, then image)
      const body = modal.querySelector('[data-mobile-modal-body]');

      // Clone header
      const header = this.container.querySelector('.custom-size-form__header');
      if (header) {
        body.appendChild(header.cloneNode(true));
      }

      // Clone unit toggle
      const unitToggle = this.container.querySelector('.custom-size-form__unit-toggle');
      if (unitToggle) {
        body.appendChild(unitToggle.cloneNode(true));
      }

      // Clone form (table first)
      const form = this.container.querySelector('[data-custom-size-form]');
      if (form) {
        body.appendChild(form.cloneNode(true));
      }

      // Clone image (after form)
      const image = this.container.querySelector('.custom-size-form__image');
      if (image) {
        body.appendChild(image.cloneNode(true));
      }

      // Add to cart button at bottom
      const addToCartBtn = document.createElement('button');
      addToCartBtn.type = 'submit';
      addToCartBtn.className = 'custom-size-mobile-modal__add-to-cart';
      addToCartBtn.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 0.5rem; vertical-align: middle;">
          <circle cx="9" cy="21" r="1"></circle>
          <circle cx="20" cy="21" r="1"></circle>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
        </svg>
        Add to Cart
      `;
      body.appendChild(addToCartBtn);

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Show modal with animation
      requestAnimationFrame(() => {
        modal.classList.add('is-open');
      });

      // Attach event listeners to modal content
      this.attachMobileModalListeners(modal);
    }

    /**
     * Close mobile modal popup
     */
    closeMobileModal() {
      const modal = document.querySelector('.custom-size-mobile-modal');
      if (!modal) return;

      modal.classList.remove('is-open');

      // Restore body scroll
      document.body.style.overflow = '';

      // Remove modal after animation
      setTimeout(() => {
        modal.remove();
      }, 300);
    }

    /**
     * Attach event listeners to mobile modal
     */
    attachMobileModalListeners(modal) {
      // Close buttons (overlay and X)
      modal.querySelectorAll('[data-mobile-modal-close]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.closeMobileModal();
        });
      });

      // Unit toggle buttons in modal
      modal.querySelectorAll('[data-unit]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.switchUnit(btn.dataset.unit);

          // Sync active state in modal
          modal.querySelectorAll('[data-unit]').forEach((b) => {
            b.classList.toggle('active', b.dataset.unit === btn.dataset.unit);
          });
        });
      });

      // Form submission in modal
      const modalForm = modal.querySelector('[data-custom-size-form]');
      modalForm?.addEventListener('submit', (e) => {
        e.preventDefault();

        // Sync values from modal to original container
        const modalInputs = modalForm.querySelectorAll('[data-custom-size-input]');
        modalInputs.forEach((modalInput) => {
          const field = modalInput.dataset.customSizeInput;
          const originalInput = this.container.querySelector(`[data-custom-size-input="${field}"]`);
          if (originalInput) {
            originalInput.value = modalInput.value;
          }
        });

        // Now validate using original container
        this.handleSubmit();
      });

      // Input changes in modal - sync to original
      modalForm?.querySelectorAll('[data-custom-size-input]').forEach((input) => {
        input.addEventListener('input', () => {
          const field = input.dataset.customSizeInput;
          const originalInput = this.container.querySelector(`[data-custom-size-input="${field}"]`);
          if (originalInput) {
            originalInput.value = input.value;
          }
          this.handleInputChange(originalInput || input);
        });
      });

      // Help icons - toggle tooltip on click
      modal.querySelectorAll('.custom-size-form__help').forEach((helpIcon) => {
        helpIcon.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Close other active tooltips
          modal.querySelectorAll('.custom-size-form__help.active').forEach((other) => {
            if (other !== helpIcon) other.classList.remove('active');
          });
          helpIcon.classList.toggle('active');
        });
      });

      // Close tooltip when clicking elsewhere
      modal.addEventListener('click', (e) => {
        if (!e.target.closest('.custom-size-form__help')) {
          modal.querySelectorAll('.custom-size-form__help.active').forEach((h) => {
            h.classList.remove('active');
          });
        }
      });

      // Add to cart button - validate and close modal
      const addToCartBtn = modal.querySelector('.custom-size-mobile-modal__add-to-cart');
      addToCartBtn?.addEventListener('click', (e) => {
        e.preventDefault();

        // Sync values from modal to original container
        const modalInputs = modal.querySelectorAll('[data-custom-size-input]');
        let isValid = true;
        let firstErrorField = null;

        const requiredFields = ['bust', 'waist', 'hips', 'bicep', 'skirt_length'];

        modalInputs.forEach((modalInput) => {
          const field = modalInput.dataset.customSizeInput;
          const originalInput = this.container.querySelector(`[data-custom-size-input="${field}"]`);
          if (originalInput) {
            originalInput.value = modalInput.value;
          }

          // Validate required fields
          if (requiredFields.includes(field) && (!modalInput.value || modalInput.value === '')) {
            isValid = false;
            modalInput.style.borderColor = '#d32f2f';
            if (!firstErrorField) firstErrorField = modalInput;
          } else {
            modalInput.style.borderColor = '';
          }
        });

        if (!isValid) {
          firstErrorField?.focus();
          firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        // Sync measurements and update hidden inputs
        this.syncMeasurements();
        this.updateHiddenInputs();

        // Close modal and trigger add to cart
        this.closeMobileModal();

        // Click the actual add to cart button on the page
        setTimeout(() => {
          const productAddBtn = document.querySelector('[data-add-to-cart]') ||
                                document.querySelector('button[name="add"]');
          if (productAddBtn && !productAddBtn.disabled) {
            productAddBtn.click();
          }
        }, 300);
      });
    }

    /**
     * Switch measurement unit (IN/CM)
     */
    switchUnit(unit) {
      if (unit === this.currentUnit) return;

      // Get all inputs - both original container and mobile modal
      const allInputs = document.querySelectorAll('[data-custom-size-input]');

      allInputs.forEach((input) => {
        const value = parseFloat(input.value);
        if (!isNaN(value) && value > 0) {
          if (unit === 'cm') {
            input.value = (value * 2.54).toFixed(1);
          } else {
            input.value = (value / 2.54).toFixed(1);
          }
        }
      });

      // Update all unit labels (original + modal)
      document.querySelectorAll('[data-unit-label]').forEach((label) => {
        label.textContent = `(${unit})`;
      });

      // Update all unit buttons active state (original + modal)
      document.querySelectorAll('[data-unit]').forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.unit === unit);
      });

      this.currentUnit = unit;
      this.syncMeasurements();
    }

    /**
     * Handle input change
     */
    handleInputChange(input) {
      const field = input.dataset.customSizeInput;
      this.measurements[field] = input.value;
      this.updateHiddenInputs();
    }

    /**
     * Sync measurements from inputs
     */
    syncMeasurements() {
      this.inputs.forEach((input) => {
        const field = input.dataset.customSizeInput;
        if (input.value) {
          this.measurements[field] = input.value;
        }
      });
    }

    /**
     * Handle "Fill out later" checkbox
     */
    handleLaterOption(e) {
      this.isLater = e.target.checked;
      if (this.form) {
        this.form.style.opacity = this.isLater ? '0.5' : '1';
        this.form.style.pointerEvents = this.isLater ? 'none' : 'auto';
      }
      this.updateHiddenInputs();
    }

    /**
     * Clear all measurements
     */
    clearForm() {
      this.inputs.forEach((input) => {
        input.value = '';
      });
      this.measurements = {};
      this.isLater = false;

      if (this.laterCheckbox) {
        this.laterCheckbox.checked = false;
      }

      if (this.form) {
        this.form.style.opacity = '1';
        this.form.style.pointerEvents = 'auto';
      }

      // Remove hidden inputs
      this.removeHiddenInputs();
    }

    /**
     * Handle form submission
     */
    handleSubmit() {
      // Validate required fields
      const requiredFields = ['bust', 'waist', 'hips', 'bicep', 'skirt_length'];
      let isValid = true;
      let firstErrorField = null;

      requiredFields.forEach((field) => {
        const input = this.container.querySelector(`[data-custom-size-input="${field}"]`);
        if (input && (!input.value || input.value === '')) {
          isValid = false;
          input.style.borderColor = '#d32f2f';
          if (!firstErrorField) firstErrorField = input;
        } else if (input) {
          input.style.borderColor = '';
        }
      });

      if (!isValid) {
        firstErrorField?.focus();
        return;
      }

      this.syncMeasurements();
      this.updateHiddenInputs();

      // Show success feedback
      const submitBtn = this.form?.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.textContent = '✓ Saved';
        submitBtn.disabled = true;
        setTimeout(() => {
          submitBtn.textContent = 'SUBMIT';
          submitBtn.disabled = false;
        }, 2000);
      }
    }

    /**
     * Update hidden inputs in the product form
     */
    updateHiddenInputs() {
      const form = document.querySelector('[data-product-form]');
      if (!form) return;

      // Remove existing
      this.removeHiddenInputs();

      // Add new hidden inputs - simplified names
      Object.entries(this.measurements).forEach(([field, value]) => {
        if (value && value !== '') {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${this.formatFieldName(field)}]`;
          input.value = `${value} ${this.currentUnit}`;
          input.dataset.customSizeHidden = '';
          form.appendChild(input);
        }
      });

      // Add unit
      const unitInput = document.createElement('input');
      unitInput.type = 'hidden';
      unitInput.name = 'properties[Unit]';
      unitInput.value = this.currentUnit;
      unitInput.dataset.customSizeHidden = '';
      form.appendChild(unitInput);

      // Add "later" flag
      if (this.isLater) {
        const laterInput = document.createElement('input');
        laterInput.type = 'hidden';
        laterInput.name = 'properties[Fill later]';
        laterInput.value = 'Yes';
        laterInput.dataset.customSizeHidden = '';
        form.appendChild(laterInput);
      }
    }

    /**
     * Remove hidden inputs
     */
    removeHiddenInputs() {
      const form = document.querySelector('[data-product-form]');
      if (form) {
        form.querySelectorAll('[data-custom-size-hidden]').forEach((el) => el.remove());
      }
    }

    /**
     * Format field name
     */
    formatFieldName(field) {
      return field
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
  }

  /**
   * Initialize
   */
  function initCustomSizeForms() {
    document.querySelectorAll('[data-custom-size-container]').forEach((container) => {
      if (!container.dataset.customSizeInitialized) {
        new CustomSizeForm(container);
        container.dataset.customSizeInitialized = 'true';
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomSizeForms);
  } else {
    initCustomSizeForms();
  }

  // Re-initialize on section reload
  document.addEventListener('shopify:section:load', initCustomSizeForms);

  window.CustomSizeForm = CustomSizeForm;
})();
